import {getWhatsappProfile} from "../../api/getWhatsappProfile";
import useAppContext from "../../hooks/useAppContext";
import useUserContext from "../../hooks/useUserContext";
import React, {useEffect, useState} from "react";
import TextAreaNext from "../forms/textAreaNext/TextAreaNext";
import "./whatsapp-profile-editor.css";
import {BigInput} from "../forms/bigInput/BigInput";
import {Button, Dropdown} from "antd";
import {setWhatsappProfile} from "../../api/setWhatsappProfile";
import Loader from "../loader/Loader";
import ImagePicker from "../forms/imagePicker/ImagePicker";
import {DownOutlined} from "@ant-design/icons";
import {ToastSkin} from "../../context/ToastContext";
import {disconnectFromWhatsappAccount} from "../../api/disconnectFromWhatsappAccount";
import useToast from "../../hooks/useToast";
import WhatsappProfilePage from "../../assets/images/whatsapp-profile-page.png";
import WhatsappProfileDetails from "../../assets/images/whatsapp-profile-details.png";
import {WHATSAPP_BUSINESS_VERTICALS} from "./verticals";
import {uploadImage} from "../../api/uploadImage";

interface WhatsappProfileEditorProps {
    isOpen: boolean;
}

const WhatsappProfileEditor = ({ isOpen }: WhatsappProfileEditorProps) => {
    const { selectedDomain } = useUserContext();
    const { data: { waba }, actions: { loadWaba } } = useAppContext();
    const [displayName, setDisplayName] = useState('');
    const [vertical, setVertical] = useState('');
    const [about, setAbout] = useState('');
    const [address, setAddress] = useState('');
    const [description, setDescription] = useState('');
    const [email, setEmail] = useState('');
    const [website, setWebsite] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    // const [image, setImage] = useState<File | null>(null);
    const [image, setImage] = useState<string | null>(null);
    const [initialImageUrl, setInitialImageUrl] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [logoUrl, setLogoUrl] = useState('');
    const { showToast, closeToast } = useToast();
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    // const [vertical, setVertical] = useState('');

    const onImageChange = async (newImage: string | null) => {
        setImage(newImage);
        if (newImage && selectedDomain) {
            setIsUploadingImage(true);
            const response = await uploadImage(selectedDomain, newImage);
            if (response.data.data) {
                setLogoUrl(response.data.data);
                setIsUploadingImage(false);
            } else if (response.data.error) {
                showToast({skin: ToastSkin.error, text: response.data.error});
                setIsUploadingImage(false);
            }
        }
    }

    const getProfile = async () => {
        setIsLoadingData(true);
        if(!waba?.data?.waba?.providerSenderId || !waba?.data?.waba?.phoneId) return;
        const response = await getWhatsappProfile(selectedDomain!);
        if(response.data.profile) {
            const profile = response.data.profile;
            profile.name && setDisplayName(profile.name);
            profile.about && setAbout(profile.about);
            profile.websites?.length && setWebsite(profile.websites[0].website?.replace('https://', '').replace('http://', ''));
            profile.description && setDescription(profile.description);
            profile.address && setAddress(profile.address);
            profile.emails?.length && setEmail(profile.emails[0].email);
            profile.logo_url && setLogoUrl(profile.logo_url);
            profile.vertical && setVertical(profile.vertical);
        }
        setIsLoadingData(false);
    }

    const onSubmit = async () => {
        if(selectedDomain) {
            try {
                setIsSubmitting(true);
                if (image) {
                    const response = await setWhatsappProfile(selectedDomain, {
                        about,
                        vertical,
                        address,
                        description,
                        email,
                        website: `https://${website.replace('https://', '').replace('http://', '')}`,
                        logoUrl
                    });
                } else {
                    const response = await setWhatsappProfile(selectedDomain, {
                        about,
                        vertical,
                        address,
                        description,
                        email,
                        website: `https://${website.replace('https://', '').replace('http://', '')}`,
                        logoUrl,
                    });
                    if(response?.data?.status === 'ONLINE:UPDATING') {
                        showToast({ skin: ToastSkin.approval, text: 'Your WhatsApp profile has been updated.' });
                    } else {
                        showToast({ skin: ToastSkin.error, text: 'Failed to update your Whatsapp profile.' });
                    }
                }
                setIsSubmitting(false);
            } catch (error: any) {
                console.log("error updating profile: ", error);
                showToast({ skin: ToastSkin.error, text: 'Failed to update your Whatsapp profile, please check the content and try again' });
                setIsSubmitting(false);
            }
        }
    }

    const onDisconnectWhatsappAccountClick = () => {
        showToast({
            skin: ToastSkin.danger,
            text: "This action is irreversible and it might have many effects on your Meta Business account, are you sure?",
            buttons: [
                { text: 'Yes', onClick: async () => {
                        if(selectedDomain) {
                            closeToast();
                            await disconnectFromWhatsappAccount(selectedDomain);
                            loadWaba();
                        }
                    }
                },
                { text: 'No, Cancel', onClick: closeToast }
            ],
        });
    }

    useEffect(() => {
        if(!isOpen || !selectedDomain) return;
        getProfile();
    }, [selectedDomain, isOpen]);

    const verticalsOptions = WHATSAPP_BUSINESS_VERTICALS.map(vertical => ({ key: vertical, label: vertical }));

    return (
        <div className="whatsapp-profile-container">
            <div className="whatsapp-profile-editor-container">
                {isLoadingData ? <Loader /> : <form id="profile-form">
                    <div className="form-description">
                        The details below are the ones your clients will see once they'll open your Whatsapp Profile.<br />
                        Please note that those details are being reviewed by Whatsapp. Sharing misleading details might cause issues with your Whatsapp Business Account, so please fill in accurate details.
                    </div>
                    <div className="form">
                        <div className="row">
                            <Dropdown className="big-dropdown" menu={{ items: verticalsOptions, className: "big-dropdown-menu", onClick: (selected) => setVertical(selected.key) }}>
                                <Button className={`big-dropdown-button ${vertical ? '' : 'placeholder'}`} icon={<DownOutlined />}>{vertical || 'Vertical'}</Button>
                            </Dropdown>
                        </div>
                        <div className="row">
                            <BigInput name="about" value={about} setValue={setAbout} placeholder="About" />
                        </div>
                        <div className="row">
                            <BigInput name="address" value={address} setValue={setAddress} placeholder="Address" />
                        </div>

                        <div className="row">
                            <BigInput name="email" value={email} setValue={setEmail} placeholder="Email" />
                        </div>
                        <div className="row">
                            <div className="input-with-text-on-top">
                                <BigInput className="website-input" name="website" value={website} setValue={setWebsite} placeholder="Website" />
                                <span>https://</span>
                            </div>
                        </div>
                        <div className="row">
                            <ImagePicker onImageConfirmed={onImageChange} initialImageUrl={initialImageUrl} previewUrl={logoUrl} isLoading={isUploadingImage} />
                            <div className="description"><TextAreaNext value={description} setValue={setDescription} label="Description" /></div>
                        </div>
                    </div>
                    <div className="profile-form-bottom-bar">
                        {/*<button type="submit" />*/}
                        <Button disabled={isUploadingImage} type="primary" size="large" onClick={onSubmit} loading={isSubmitting}>Submit</Button>
                    </div>
                </form>}
            </div>
            <div className="whatsapp-profile-preview">
                <div className="whatsapp-preview-images">
                    <img src={WhatsappProfilePage} />
                    <div className="whatsapp-profile-details">
                        {logoUrl ? <img className="preview-image" src={logoUrl} /> : null}
                        <div className="display-name">{displayName}</div>
                        <div className="vertical">{vertical}</div>
                        <div className="about">{about}</div>
                        <div className="contact-details">
                            <img className="contact-details-background" src={WhatsappProfileDetails} />
                            <div className="contact-details-content">
                                <div>{waba?.data?.waba?.phoneNumber}</div>
                                <div>https://{website}</div>
                                <div>{email}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WhatsappProfileEditor;